import {TICKET_SEARCH_TS_EXCLUSIVE_ADJUSTMENT, TicketOperation, TicketOperationResult} from '@app/layout/sites/tickets/tickets.interface';
import {AlertTicket, BaseTicket, EscalatedTicket, Ticket, TicketAPIResultPartial, TicketFaultStatus, TicketId, TicketLinkDirection, TicketStatusId, TicketType} from '@shared/services/api/tickets/tickets.interface';
import moment, {Moment} from 'moment-timezone';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ConfirmationComponent} from '@shared/components/confirmation/confirmation.component';
import {ArrayUtils, CollectionUtils} from '@shared/util/collections/collection-utils';
import {I18nMessage} from '@shared/api/model/i18n';
import {Task, TaskId} from '@app/shared/services/api/task.interface';
import {TicketDataCaches} from "@shared/data-stores/ticket-data-caches";
import {Injector} from "@angular/core";
import {StringUtils} from '@app/shared/util/general/string-utils';
import {SiteId} from '@app/shared/services/api/access-sites.interface';

export interface SplitLastWordParts {
  firstPart: string;
  lastWordPart: string;
}

const sortWeightByStatusId: Map<TicketStatusId, number> = new Map([
  [TicketStatusId.STATUS_NEW, 1],
  [TicketStatusId.STATUS_IN_PROGRESS, 2],
  [TicketStatusId.STATUS_ON_HOLD, 6],
  [TicketStatusId.STATUS_RESOLVED, 7],
  [TicketStatusId.STATUS_NOT_DOING, 8]
]);


const sortWeightByFaultStatus: Map<TicketFaultStatus, number> = new Map([
  [TicketFaultStatus.FAULT, 1],
  [TicketFaultStatus.RECOVERED, 2],
]);

export interface AttachmentUrlData {
  ticket_id: string;
  attachment_id: string;
}

export type TicketComparator<T extends Ticket> = (a: T, b: T) => number;

export enum TicketArchivedDisplayState {
  NOT_ARCHIVED = 'false',
  ARCHIVED = 'true',
  EITHER = 'either',
}

export interface LinkActionToAlertsCallbackFns {
  onConfirmUpdateLink: <T>(alerts: AlertTicket[] | null) => T;
}

export class TicketUtils {

  public static readonly MENTION_CHARACTER = '@';
  public static readonly TICKET_URL_SEGMENT = 'tickets';
  public static readonly ACTION_URL_SEGMENT = 'escalated';
  public static readonly ALERT_URL_SEGMENT = 'alerts';
  public static readonly SEARCH_URL_SEGMENT = 'search';
  public static readonly ATTACHMENT_URL_SEGMENT = 'attachments';
  public static readonly ATTACHMENT_PREVIEW_DISPLAY_HEIGHT = 60;
  public static readonly ATTACHMENT_PREVIEW_DISPLAY_WIDTH = 100;

  private static getComparatorByName<T extends Ticket>(name: string): TicketComparator<T> {
    const fieldName = name.toLowerCase();
    switch (fieldName) {
      case 'inbox':
        return TicketUtils.getCompareTicketsByBooleanFn(fieldName);
      case 'status':
        return TicketUtils.compareTicketsByStatus;
      case 'fault_status':
        return <TicketComparator<T>>TicketUtils.compareTicketsByFaultStatus;
      case 'created_at':
        return TicketUtils.getCompareTicketsByTsFn('created_at');
      case 'updated_at':
        return TicketUtils.getCompareTicketsByTsFn(fieldName);
      case 'priority_id':
        return <TicketComparator<T>>TicketUtils.compareTicketsByPriority;
      case 'summary':
        return TicketUtils.getCompareTicketsByStringFn(fieldName);
      default:
        throw new Error('Cannot find ticket comparator for field ' + name);
    }
  }

  public static closeModal<T extends Ticket>(result: TicketOperationResult<T>, archivedDisplay: TicketArchivedDisplayState = TicketArchivedDisplayState.NOT_ARCHIVED): boolean {
    return result.operation === TicketOperation.DELETE
        || (result.operation === TicketOperation.UPDATE &&
            ((archivedDisplay === TicketArchivedDisplayState.ARCHIVED && !result.ticket.ticket_archived) ||
             (archivedDisplay === TicketArchivedDisplayState.NOT_ARCHIVED && result.ticket.ticket_archived)))
        || result.operation === TicketOperation.DISMISS;
  }

  public static compareTicketsByStatus(a: Ticket, b: Ticket): number {
    const sortWeightForA = sortWeightByStatusId.get(a.status?.status_id) || Number.MAX_VALUE;
    const sortWeightForB = sortWeightByStatusId.get(b.status?.status_id) || Number.MAX_VALUE;
    return sortWeightForA - sortWeightForB;
  }

  public static compareTicketsByFaultStatus(a: AlertTicket, b: AlertTicket): number {
    const sortWeightForA = sortWeightByFaultStatus.get(a.fault_status) || Number.MAX_VALUE;
    const sortWeightForB = sortWeightByFaultStatus.get(b.fault_status) || Number.MAX_VALUE;
    return sortWeightForA - sortWeightForB;
  }

  private static getCompareTicketsByBooleanFn<T extends Ticket>(field: string): TicketComparator<T> {
    return (a: T, b: T) => {
      const valueA = !!a[field] ? 1 : 0;
      const valueB = !!b[field] ? 1 : 0;
      return valueA - valueB;
    };
  }

  private static getCompareTicketsByTsFn<T extends Ticket>(field: string): TicketComparator<T> {
    return (a: T, b: T) => {
      return moment(a[field]).diff(moment(b[field]));
    };
  }

  public static getCompareTicketsByStringFn<T extends Ticket>(field: string): TicketComparator<T> {
    return (a: T, b: T) => {
      const valueA: string = a[field];
      const valueB: string = b[field];
      if (valueA != null) {
        return valueA.localeCompare(valueB);
      } else {
        return valueB != null ? 1 : 0;
      }
    };
  }

  public static compareTicketsByPriority(a: EscalatedTicket, b: EscalatedTicket): number {
    const priorityValueA = a.priority?.priority_id || Number.MAX_VALUE;
    const priorityValueB = b.priority?.priority_id || Number.MAX_VALUE;
    return priorityValueA - priorityValueB;
  }

  public static sortTickets<T extends Ticket>(tickets: Partial<T>[], sortBy: string[]): void {
    const sortByProcessed: [TicketComparator<T>, number][] = sortBy.map(item => {
      const [name, order] = item.split(' ');
      const comparator = TicketUtils.getComparatorByName(name);
      const modifier = order === 'DESC' ? -1 : 1;
      return [comparator, modifier];
    });

    tickets.sort((a: T, b: T) => {
      for (const [comparator, modifier] of sortByProcessed) {
        const result = comparator(a, b) * modifier;
        if (result !== 0) {
          return result;
        }
      }
      return 0;
    });
  }

  public static registerTicketInMap(ticket: Ticket, ticketsById: Map<string, Ticket>, skipLinkedTickets?: boolean) {
    if (ticket.ticket_id) {
      ticketsById.set(ticket.ticket_id, ticket);
    }
    if (!skipLinkedTickets) {
      ticket.ticket_links?.forEach(link => {
        if (!ticketsById.has(link.ticket.ticket_id)) {
          TicketUtils.registerTicketInMap(link.ticket, ticketsById);
        }
      });
    }
  }

  public static mergeTicketOperationResults<T extends Ticket>(oldResult: TicketOperationResult<T> | null, newResult: TicketOperationResult<T>): TicketOperationResult<T> {
    return {
      operation: newResult.operation,
      ticket: newResult.ticket,
      affectedTickets: ArrayUtils.concat(oldResult?.affectedTickets, newResult?.affectedTickets),
      newAlertTasks: ArrayUtils.concat(oldResult?.newAlertTasks, newResult?.newAlertTasks),
    };
  }

  public static getTaskIdsFromActions(actions: EscalatedTicket[] | null): number[] {
    const taskIds = new Set<number>();
    actions?.forEach(action => {
      const linkedAlerts = TicketUtils.getActionLinkedAlerts(action);
      linkedAlerts?.forEach(alert => taskIds.add(alert.rule_id));
    });
    return Array.from(taskIds);
  }

  public static getTaskIdsFromAlerts(alerts: AlertTicket[] | null): number[] {
    const taskIds = new Set<number>();
    alerts?.forEach(alert => {
      taskIds.add(alert.rule_id);
      const linkedAction = TicketUtils.getAlertLinkedAction(alert);
      if (linkedAction) {
        TicketUtils.getTaskIdsFromActions([linkedAction]).forEach(taskId => taskIds.add(taskId));
      }
    });
    return Array.from(taskIds);
  }


  public static getDirectTaskIdFromAlert(alert: AlertTicket): number {
    return alert.rule_id;
  }

  public static getAlertLinkedAction(alert: AlertTicket): EscalatedTicket {
    return <EscalatedTicket>CollectionUtils.only(TicketUtils.getLinkedTicketsByDirection(alert, TicketLinkDirection.TO_TARGET));
  }

  public static getActionLinkedAlerts(action: EscalatedTicket): AlertTicket[] {
    return <AlertTicket[]>TicketUtils.getLinkedTicketsByDirection(action, TicketLinkDirection.TO_SOURCE);
  }

  public static getTaskIdsByTicketId(escalatedTickets: Iterable<EscalatedTicket>): Map<string, number[]> {
    return CollectionUtils.indexBy(escalatedTickets, 'ticket_id', ticket => TicketUtils.getTaskIdsFromActions([ticket]));
  }

  public static linkActionToAlerts(action: Partial<EscalatedTicket> | null,
                                   alerts: AlertTicket[] | null,
                                   modalService: NgbModal,
                                   { onConfirmUpdateLink }: LinkActionToAlertsCallbackFns) {
    const alreadyLinkedAlerts: AlertTicket[] = [];
    alerts?.forEach(alert => {
      const linkedAction = TicketUtils.getAlertLinkedAction(alert);
      if (linkedAction && linkedAction.ticket_id !== action?.ticket_id) {
        alreadyLinkedAlerts.push(alert);
      }
    });

    if (alreadyLinkedAlerts.length) {
      let confirmBodyMessage: I18nMessage;
      if (action?.ticket_id) {
        confirmBodyMessage = {
          messageKey: 'Do you want to update Alert links to this Action',
          parameters: {
            actionSummary: action.summary
          }
        };
      } else {
        confirmBodyMessage = {
          messageKey: 'Do you want to update Alert links to the new Action?'
        };
      }
      ConfirmationComponent.createModal(modalService, {
        title: {
          messageKey: 'Update Alert Links'
        },
        header: {
          messageKey: 'The following alerts are currently linked to different Actions',
        },
        items: alreadyLinkedAlerts.map(alert => ({
          messageKey: 'Alert is linked to Action',
          parameters: {
            alertSummary: alert.summary,
            actionSummary: TicketUtils.getAlertLinkedAction(alert).summary,
          }
        })),
        body: confirmBodyMessage,
        confirmFn: () => onConfirmUpdateLink && onConfirmUpdateLink(alerts),
        yesText: {
          messageKey: 'Update Alert Links'
        }
      });
    } else {
      onConfirmUpdateLink && onConfirmUpdateLink(alerts);
    }
  }

  private static getLinkedTicketsByDirection<T extends Ticket>(ticket: T, requestedDirection: TicketLinkDirection): Ticket[] {
    const linkedTickets = [];
    ticket.ticket_links?.forEach(({ direction, ticket }) => {
      if (direction === requestedDirection) {
        linkedTickets.push(ticket);
      }
    });
    return linkedTickets;
  }

  public static convertAPIResultToOperationResult(operation: TicketOperation,
                                                  apiResult: TicketAPIResultPartial<Ticket>,
                                                  newAlertTasks?: Task[]): TicketOperationResult<Ticket> {
    return {
      operation,
      ticket: apiResult.ticket,
      affectedTickets: apiResult.affected_tickets,
      newAlertTasks,
    };
  }

  public static getPathByTicketType(ticketType: TicketType): string {
    switch (ticketType) {
      case TicketType.ALERT:
        return TicketUtils.ALERT_URL_SEGMENT;
      case TicketType.ESCALATED:
        return TicketUtils.ACTION_URL_SEGMENT;
      default:
        throw new Error('Cannot recognise ticket type: ' + ticketType + ' as path.');
    }
  }

  public static getSearchRouteByTicketType(ticketType: TicketType): string[] {
    return ['/', TicketUtils.TICKET_URL_SEGMENT, TicketUtils.getPathByTicketType(ticketType), TicketUtils.SEARCH_URL_SEGMENT];
  }

  public static makeTicketDataCachesInjector(ticketDataCaches: TicketDataCaches, parentInjector: Injector): Injector {
    return Injector.create({
      providers: [{ provide: TicketDataCaches, useValue: ticketDataCaches }],
      parent: parentInjector
    });
  }

  public static makeExportTicketLink(siteId: number, ticketId: string): string {
    return [this.getURLOrigin(), 'chromeless', this.makeEscalatedTicketPath(siteId, ticketId)].join('/');
  }

  public static makeRootAttachmentPath(siteId: number, ticketId: string, attachmentId: string, attachmentName: string): string {
    return [this.makeRootEscalatedTicketPath(siteId, ticketId), this.ATTACHMENT_URL_SEGMENT,
      attachmentId, StringUtils.updatedEncodeURIComponent(attachmentName)].join('/')
  }

  public static makeAttachmentLink(siteId: number, ticketId: string, attachmentId: string, attachmentName: string): string {
    return this.getURLOrigin() + this.makeRootAttachmentPath(siteId,ticketId, attachmentId, attachmentName);
  }

  public static getAttachmentUrlData(urlPathNameSegments: string[]): AttachmentUrlData | null {
    return this.isAttachmentUrl(urlPathNameSegments)
      ? {
          ticket_id: urlPathNameSegments[3],
          attachment_id: urlPathNameSegments[5]
        }
      : null;
  }

  public static isActionUrl(urlPathNameSegments: string[]): boolean {
    return urlPathNameSegments[0] === TicketUtils.TICKET_URL_SEGMENT
        && urlPathNameSegments[1] === TicketUtils.ACTION_URL_SEGMENT;
  }

  public static isAttachmentUrl(urlPathNameSegments: string[]): boolean {
    return this.isActionUrl(urlPathNameSegments)
        && urlPathNameSegments[4] === TicketUtils.ATTACHMENT_URL_SEGMENT
        && urlPathNameSegments[5]?.length > 0;
  }

  public static makeAlertTicketUrl(siteId: SiteId, ticketId: TicketId): URL {
    const segments = this.makeTicketPathSegments(siteId, ticketId, TicketType.ALERT);
    return new URL(segments.join('/'), this.getURLOrigin());
  }

  public static makeEscalatedTicketUrl(siteId: SiteId, ticketId: string): URL {
    return new URL(this.makeEscalatedTicketPath(siteId, ticketId), this.getURLOrigin());
  }

  public static makeRootEscalatedTicketPath(siteId: number, ticketId: string) {
    return '/' + this.makeEscalatedTicketPath(siteId, ticketId);
  }

  private static makeEscalatedTicketPath(siteId: number, ticketId: string): string {
    const segments = this.makeTicketPathSegments(siteId, ticketId, TicketType.ESCALATED);
    return segments.join('/');
  }

  private static makeTicketPathSegments(siteId: SiteId, ticketId: TicketId, ticketType: TicketType): string[] {
    let typeSegment: string;
    if (ticketType === TicketType.ALERT) {
      typeSegment = this.ALERT_URL_SEGMENT;
    } else if (ticketType === TicketType.ESCALATED) {
      typeSegment = this.ACTION_URL_SEGMENT;
    } else {
      throw new Error('Unrecognized ticketType: ' + ticketType);
    }
    return [this.TICKET_URL_SEGMENT, typeSegment, siteId.toString(), ticketId];
  }

  private static getURLOrigin(): string {
    return new URL(window.location.href).origin;
  }

  public static splitLastWord(wholeString: string): SplitLastWordParts {
    const SPACE = ' ';
    const summaryWords: string[] = wholeString.trim().split(SPACE);
    const lastWord = summaryWords.pop();
    return {firstPart: summaryWords.join(SPACE), lastWordPart: lastWord };
  }

  public static getTaskIdsFromTicket(ticket: Ticket): TaskId[] {
    if (ticket.type === TicketType.ALERT) {
      return [TicketUtils.getDirectTaskIdFromAlert(<AlertTicket>ticket)];
    } else if (ticket.type === TicketType.ESCALATED) {
      return TicketUtils.getTaskIdsFromActions([<EscalatedTicket>ticket]);
    }
  }

  public static getOtherEscalatedTicketsCount(escalatedTicketsGroupByRuleId: Map<TaskId, EscalatedTicket[]>, relatedEscalatedTicket: EscalatedTicket | null): number {
    const escalatedTicketIdSet = new Set<TicketId>();
    escalatedTicketsGroupByRuleId?.forEach(escalatedTickets => escalatedTickets.forEach(escalatedTicket => escalatedTicketIdSet.add(escalatedTicket.ticket_id)));
    escalatedTicketIdSet.delete(relatedEscalatedTicket?.ticket_id);
    return escalatedTicketIdSet.size;
  }

  public static ticketLinkChanged(ticket: Ticket, ticketOperationResult: TicketOperationResult<Ticket>): boolean {
    if (ticket.ticket_id === ticketOperationResult.ticket?.ticket_id) {
      return ticket.ticket_links !== ticketOperationResult.ticket.ticket_links;
    } else {
      return ticketOperationResult.affectedTickets?.some(affectedTicket =>
        affectedTicket.ticket_id === ticket.ticket_id && 'ticket_links' in affectedTicket) ?? false;
    }
  }

  public static isEscalatedTicket(ticket: BaseTicket): ticket is EscalatedTicket {
    return ticket.type === TicketType.ESCALATED;
  }

  public static isAlertTicket(ticket: BaseTicket): ticket is AlertTicket {
    return ticket.type === TicketType.ALERT;
  }
}
